import { Link, NavLink, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode } from "react";
import { api } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { Ornament } from "./Ornament";

export function Shell({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();

  const logout = useMutation({
    mutationFn: () => api.post<void>("/api/auth/logout"),
    onSuccess: () => {
      qc.setQueryData(["me"], null);
      qc.removeQueries({ queryKey: ["recipes"] });
      qc.removeQueries({ queryKey: ["meal-plan"] });
      qc.removeQueries({ queryKey: ["regulars"] });
      qc.removeQueries({ queryKey: ["shopping-list"] });
      nav("/login", { replace: true });
    },
  });

  const today = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  return (
    <div className="min-h-screen">
      <header className="relative">
        <div className="mx-auto max-w-[1480px] px-8 pt-12 md:px-12">
          <div className="flex items-end justify-between gap-8 pb-5">
            <div className="flex-1 animate-fade-in">
              <div className="label mb-3 text-sienna">
                Volume I &nbsp;·&nbsp; The Kitchen Journal
              </div>
              <Link to="/" className="block">
                <h1 className="font-display text-[88px] font-light leading-[0.85] tracking-[-0.04em] text-ink md:text-[112px]">
                  <span className="display-italic">Maison</span>
                </h1>
              </Link>
            </div>
            <div
              className="hidden flex-col items-end animate-fade-in opacity-0 text-right md:flex"
              style={{ animationDelay: "200ms" }}
            >
              <Ornament className="mb-3 h-4 w-20 text-sienna" variant="default" />
              <div className="label text-ink">{me?.household.name}</div>
              <div className="label mt-1 text-ink-walnut">{today}</div>
            </div>
          </div>

          <span className="hairline-ink" />

          <nav className="flex flex-wrap items-center justify-between gap-6 py-4">
            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `label transition-colors duration-200 ${
                    isActive
                      ? "text-sienna"
                      : "text-ink hover:text-sienna"
                  }`
                }
              >
                Recipes
              </NavLink>
              <NavLink
                to="/plan"
                className={({ isActive }) =>
                  `label transition-colors duration-200 ${
                    isActive
                      ? "text-sienna"
                      : "text-ink hover:text-sienna"
                  }`
                }
              >
                Plan
              </NavLink>
              <NavLink
                to="/regulars"
                className={({ isActive }) =>
                  `label transition-colors duration-200 ${
                    isActive
                      ? "text-sienna"
                      : "text-ink hover:text-sienna"
                  }`
                }
              >
                Regulars
              </NavLink>
              <NavLink
                to="/shopping-list"
                className={({ isActive }) =>
                  `label transition-colors duration-200 ${
                    isActive
                      ? "text-sienna"
                      : "text-ink hover:text-sienna"
                  }`
                }
              >
                List
              </NavLink>
              <NavLink
                to="/basket-plan"
                className={({ isActive }) =>
                  `label transition-colors duration-200 ${
                    isActive
                      ? "text-sienna"
                      : "text-ink hover:text-sienna"
                  }`
                }
              >
                Basket
              </NavLink>
              <NavLink
                to="/invite"
                className={({ isActive }) =>
                  `label transition-colors duration-200 ${
                    isActive
                      ? "text-sienna"
                      : "text-ink hover:text-sienna"
                  }`
                }
              >
                Household
              </NavLink>
            </div>
            <div className="flex items-center gap-8">
              <span className="label text-ink-walnut">
                {me?.user.display_name}
              </span>
              <button
                type="button"
                onClick={() => logout.mutate()}
                className="label text-ink transition-colors duration-200 hover:text-sienna"
              >
                Sign out
              </button>
            </div>
          </nav>

          <span className="hairline-ink" />
        </div>
      </header>

      <main className="mx-auto max-w-[1480px] px-8 py-20 md:px-12 md:py-24">
        {children}
      </main>

      <footer className="mx-auto max-w-[1480px] px-8 pb-12 md:px-12">
        <span className="hairline mb-6" />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <span className="label text-ink-muted">
            Maison &nbsp;·&nbsp; privately printed
          </span>
          <Ornament className="h-3 w-16 text-ink/30" variant="rule" />
          <span className="label text-ink-muted">Est. MMXXVI</span>
        </div>
      </footer>
    </div>
  );
}
